import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { requireGroupId, extractError } from '../../store/thunkHelpers';
import { relationshipApi, ERROR_DUPLICATE_RELATIONSHIP } from './relationshipApi';
import type {
  Relationship,
  CreateBidirectionalPayload,
  DeleteBidirectionalPayload,
  SetPrimaryPayload,
  SetPinnedPayload,
  UpdateDescriptionsPayload,
} from './relationshipTypes';

/** Re-export per i consumer storici (toast handler ecc.). Fonte: relationshipApi. */
export { ERROR_DUPLICATE_RELATIONSHIP };

/**
 * Fetch bidirezionale: un solo GET al backend (RelationshipLookup),
 * il client normalizza gli inverse e deduplica contro i direct.
 * Argomento className DEVE essere FQN (es. "it.dvel.crm.core.model.Person").
 */
export const fetchRelationships = createAsyncThunk<
  Relationship[],
  { className: string; classPK: number },
  { state: RootState }
>(
  'relationships/fetchByEntity',
  async ({ className, classPK }, { getState, rejectWithValue }) => {
    try {
      const groupId = requireGroupId(getState);
      return await relationshipApi.getByEntityBidirectional(groupId, className, classPK);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

/**
 * Crea relazione bidirezionale dispatchando al global slice. Per i flussi
 * che lavorano su uno scope isolato (binder edit-mode in embed timeline)
 * usare invece `relationshipApi.createBidirectional` direttamente.
 */
export const createBidirectionalRelationship = createAsyncThunk<
  Relationship[],
  CreateBidirectionalPayload,
  { state: RootState }
>(
  'relationships/createBidirectional',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const groupId = requireGroupId(getState);
      return await relationshipApi.createBidirectional(groupId, payload);
    } catch (err: unknown) {
      if (err instanceof Error && err.message === ERROR_DUPLICATE_RELATIONSHIP) {
        return rejectWithValue(ERROR_DUPLICATE_RELATIONSHIP);
      }
      return rejectWithValue(extractError(err));
    }
  }
);

/**
 * Toggle del flag `primary` sulla relazione. La response del backend contiene
 * il record aggiornato; lo slice (extraReducer) applica anche il demote locale
 * dei sibling della stessa coppia quando `primary=true`, replicando l'invariante
 * "max 1 primary" del backend senza dover refetchare.
 *
 * Payload ridondante (className1/classPK1/className2): il backend non li
 * richiede sulla PATCH (sono read-only), ma lo slice li usa per filtrare i
 * sibling locali da demote.
 */
export const setRelationshipPrimary = createAsyncThunk<
  Relationship,
  SetPrimaryPayload,
  { state: RootState }
>(
  'relationships/setPrimary',
  async (payload, { rejectWithValue }) => {
    try {
      return await relationshipApi.setPrimary(payload.relationshipId, payload.primary);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

/**
 * Toggle del flag `pinned` sulla relazione (azione POST pin/unpin). La response
 * contiene il record aggiornato; lo slice (extraReducer) applica anche il
 * "demote" locale degli altri record della stessa sorgente (className1,
 * classPK1) quando `pinned=true`, replicando l'invariante "max 1 pinnata per
 * sorgente" del backend senza refetchare.
 */
export const setRelationshipPinned = createAsyncThunk<
  Relationship,
  SetPinnedPayload,
  { state: RootState }
>(
  'relationships/setPinned',
  async (payload, { rejectWithValue }) => {
    try {
      return payload.pinned
        ? await relationshipApi.pin(payload.relationshipId)
        : await relationshipApi.unpin(payload.relationshipId);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

/**
 * Modifica le due descrizioni di una coppia (FILO-20): due PATCH, prima il
 * record della riga e poi il suo inverse. Non sono atomiche: se la seconda
 * fallisce la prima resta salvata, e il chiamante deve rileggere le relazioni
 * per mostrare lo stato vero invece di quello che l'utente ha scritto.
 */
export const updateRelationshipDescriptions = createAsyncThunk<
  { direct: Relationship; reverse: Relationship | null },
  UpdateDescriptionsPayload,
  { state: RootState }
>(
  'relationships/updateDescriptions',
  async (payload, { rejectWithValue }) => {
    try {
      const direct = await relationshipApi.updateDescription(
        payload.relationshipId, payload.description);
      const reverse = payload.reverseRelationshipId
        ? await relationshipApi.updateDescription(
          payload.reverseRelationshipId, payload.reverseDescription)
        : null;
      return { direct, reverse };
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

/**
 * Cancella relazione bidirezionale dispatchando al global slice. Per i
 * flussi che lavorano su uno scope isolato usare invece
 * `relationshipApi.deleteBidirectional` direttamente.
 */
export const deleteBidirectionalRelationship = createAsyncThunk<
  number[],
  DeleteBidirectionalPayload,
  { state: RootState }
>(
  'relationships/deleteBidirectional',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const groupId = requireGroupId(getState);
      return await relationshipApi.deleteBidirectional(groupId, payload);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);
